import {
  Box,
  Divider,
  Stack,
  Typography,
  colors,
  useTheme,
} from "@mui/material";
import React from "react";
import { usePerksManager } from "./usePerksManager";
import { useAppStore } from "@/global/store";
import {
  cartManagerState,
  selectPerkItemSelected,
  selectPerkItemSelectedByPerkCode,
} from "@/global/store/data/purchaseSeats/cartManager";
import { PerkItemType, PerkParentType } from "./perks.types";
import {
  PerkItemActionElements,
  PerkItemCardWrapper,
  PerkItemEventDetail,
  PerkItemPrice,
  PerkItemTitleDesc,
  PerkParentItemIcon,
  PerkItemLogo,
} from "./perks.components";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { useDispatch } from "react-redux";

/*
  +------------------------------------------------------------------------------+
  * [ GENERIC PERK ITEM CARD ]
  +------------------------------------------------------------------------------+ */
export const PerkItemCard = (props: {
  perkItem: PerkItemType;
  perkParent?: PerkParentType;
  hideParentIcon?: boolean;
  hideEventDetails?: boolean;
  hideTitleDesc?: boolean;
  hideActionItems?: boolean;
  disableSelectedStyle?: boolean;
  hideErrorMessage?: boolean;
  isDisabled?: boolean;
  hidePrice?: boolean;
}) => {
  const { lg: lgDown } = useCssMediaQuery("down");
  const dispatch = useDispatch();
  const theme = useTheme();

  // states and event handlers
  const perkItemSelected = useAppStore(selectPerkItemSelected(props.perkItem)); // prettier-ignore
  const isPerkItemSelected = perkItemSelected !== null;
  const selectedPerkItemsByGroup = useAppStore(
    selectPerkItemSelectedByPerkCode(props?.perkParent?.perkCode as string)
  );
  const [manualSeatCount, setManualSeatCount] = React.useState(props?.perkItem?.seatCount || 0 ); // prettier-ignore
  const { onSelectPerk } = usePerksManager();

  // flag to check if perk is rendered in "Your Tickets" section or in "Perks" section
  const isRenderedInCheckout = props?.hideEventDetails;

  const onChangeManualSeatCount = (value: number) => {
    setManualSeatCount(value);
    if (isPerkItemSelected) {
      const newPerkItem = { ...props.perkItem, seatCount: value };
      dispatch(cartManagerState.actions.updatePerkItem(newPerkItem));
    }
  };

  return (
    <PerkItemCardWrapper
      isDisabled={
        props?.isDisabled ||
        (props.perkParent?.groupingType == "single-select" &&
          selectedPerkItemsByGroup.length > 0 &&
          perkItemSelected?.id !== props.perkItem.id)
      }
      isSelected={props?.disableSelectedStyle ? false : isPerkItemSelected}
    >
      <Stack direction={lgDown ? "column" : "row"} alignItems={"center"}>
        <Stack
          p={2}
          direction={"row"}
          alignItems={"center"}
          spacing={1}
          width={"100%"}
        >
          {props?.perkParent && !props.hideParentIcon && (
            <PerkParentItemIcon
              perkParent={props.perkParent}
              style={{ color: colors.grey[600], fontSize: "32px" }}
            />
          )}

          {props?.perkItem?.logoUrl ? (
            <PerkItemLogo logoUrl={props?.perkItem?.logoUrl} />
          ) : null}

          {!props?.hideTitleDesc ? (
            <PerkItemTitleDesc
              perkItem={props.perkItem}
              perkParent={props?.perkParent}
            />
          ) : null}

          {!props?.hideEventDetails ? (
            <PerkItemEventDetail perkItem={props.perkItem} />
          ) : null}

          <Box flexGrow={1} />

          <Box>
            {!props?.hidePrice ? (
              <PerkItemPrice
                perkItem={
                  isRenderedInCheckout
                    ? props.perkItem
                    : { ...props?.perkItem, seatCount: manualSeatCount }
                }
              />
            ) : null}
          </Box>
        </Stack>

        {!props?.hideActionItems ? (
          <PerkItemActionElements
            perkItem={props.perkItem}
            manualSeatCount={manualSeatCount}
            onSelectPerk={onSelectPerk}
            onChangeManualSeatCount={onChangeManualSeatCount}
          />
        ) : null}
      </Stack>

      {props?.perkParent?.groupingType == "single-select" &&
      props?.perkItem?.description ? (
        <>
          <Divider />
          <Stack p={2}>
            <Typography
              variant="body2"
              lineHeight={1.1}
              mt={0.5}
              color={"text.primary"}
            >
              {props.perkItem.description}
            </Typography>
          </Stack>
        </>
      ) : null}

      {!props?.hideErrorMessage && props?.perkItem?.errorMessage && (
        <Box px={2} pb={2}>
          <Typography variant="caption" color={theme.palette.error.main}>
            Sold Out
          </Typography>
        </Box>
      )}
    </PerkItemCardWrapper>
  );
};
